import { Transport, TransportRequest, TransportResponse } from "../../../transport/Transport"
import { TimerSample } from "../../../metrics/Timer"
import { ApiCallMetrics, ApiOperation } from "../../../metrics/monitoring/MonitoringMetricRegistry"
import {
  EntityEvaluateRequestDto,
  EntityEvaluateResponseDto,
  WorkspaceEvaluateRequestDto,
  WorkspaceEvaluateResponseDto
} from "../model/dto"
import RemoteEvaluateClient from "./RemoteEvaluateClient"

const CONTENT_TYPE = "application/json"
const NO_CONTENT = 204

/**
 * XHR-backed remote evaluation client. The transport is expected to be intercepted with the SDK
 * headers. The workspace endpoint returns HTTP 204 for not-modified, resolved here as `null`.
 */
export class HttpRemoteEvaluateClient implements RemoteEvaluateClient {
  constructor(
    private readonly workspaceUrl: string,
    private readonly entityUrl: string,
    private readonly transport: Transport
  ) {}

  async evaluateIfModified(request: WorkspaceEvaluateRequestDto): Promise<WorkspaceEvaluateResponseDto | null> {
    const transportRequest = this.createRequest(this.workspaceUrl, request)
    const response = await this.execute(transportRequest, "evaluate.workspace")
    return this.handleWorkspaceResponse(response)
  }

  async evaluateEntities(request: EntityEvaluateRequestDto): Promise<EntityEvaluateResponseDto> {
    const transportRequest = this.createRequest(this.entityUrl, request)
    const response = await this.execute(transportRequest, "evaluate.entities")
    return this.handleResponse<EntityEvaluateResponseDto>(response)
  }

  private createRequest(url: string, body: unknown): TransportRequest {
    return TransportRequest.builder()
      .url(url)
      .method("POST")
      .addHeader("Content-Type", CONTENT_TYPE)
      .body(JSON.stringify(body))
      .build()
  }

  private async execute(request: TransportRequest, operation: ApiOperation): Promise<TransportResponse> {
    const sample = TimerSample.start()
    try {
      const response = await this.transport.send(request)
      ApiCallMetrics.record(operation, sample, response)
      return response
    } catch (e) {
      ApiCallMetrics.record(operation, sample, null)
      throw e
    }
  }

  private handleWorkspaceResponse(response: TransportResponse): WorkspaceEvaluateResponseDto | null {
    if (response.statusCode === NO_CONTENT) {
      return null
    }
    return this.handleResponse<WorkspaceEvaluateResponseDto>(response)
  }

  private handleResponse<T>(response: TransportResponse): T {
    if (!response.isSuccessful()) {
      throw new Error(`Http status code: ${response.statusCode}`)
    }
    if (response.body === null) {
      throw new Error("Response body is empty")
    }
    return JSON.parse(response.body) as T
  }
}
